/**
 * Runtime configuration — loaded from /config.json before the app renders.
 * API origins are deployment configuration, never build-time constants, so a
 * single build artifact can serve any origin/hostname later. Clinic tenancy
 * is NEVER derived from the hostname: the active clinic always travels as
 * the x-clinic-id header, validated server-side against live memberships.
 */

import { TimeoutError } from './api/errors';
import { withDeadline } from './api/deadline';

export interface RuntimeConfig {
  /** Clinic API origin (cms-backend, /v1). */
  clinicApiUrl: string;
  /** Auth service origin (login, OTP, refresh, clinic-admin surfaces). */
  authApiUrl: string;
  /**
   * ZOY-696: the Chat platform origin (Room Events, mediasoup signalling) the
   * therapist's hosted-video surface talks to with the clinic token. Optional:
   * without it, Enter session still opens the door but the video surface says
   * the platform is not configured for this deployment.
   */
  chatApiUrl?: string;
  /** Free-form environment label for diagnostics. */
  environment?: string;
}

let current: RuntimeConfig | null = null;

function invalid(reason: string): Error {
  return new Error(`config.json is invalid: ${reason}`);
}

export function parseRuntimeConfig(raw: unknown): RuntimeConfig {
  if (typeof raw !== 'object' || raw === null) throw invalid('not an object');
  const o = raw as Record<string, unknown>;
  for (const key of ['clinicApiUrl', 'authApiUrl'] as const) {
    const v = o[key];
    if (typeof v !== 'string' || !/^https?:\/\//.test(v)) {
      throw invalid(`${key} must be an http(s) origin`);
    }
  }
  return {
    clinicApiUrl: (o.clinicApiUrl as string).replace(/\/+$/, ''),
    authApiUrl: (o.authApiUrl as string).replace(/\/+$/, ''),
    chatApiUrl:
      typeof o.chatApiUrl === 'string' && /^https?:\/\//.test(o.chatApiUrl)
        ? o.chatApiUrl.replace(/\/+$/, '')
        : undefined,
    environment: typeof o.environment === 'string' ? o.environment : undefined,
  };
}

/**
 * ZOY-466: nothing renders until this settles — main.tsx gates the whole app
 * on it and #root is empty in the HTML — so a stalled config.json left a
 * permanently blank page. The bilingual error-and-retry screen already
 * existed; it just hung off `.catch()`, which a stall never reaches. The
 * deadline makes it reachable.
 */
export async function loadRuntimeConfig(): Promise<RuntimeConfig> {
  const deadline = withDeadline();
  let res: Response;
  try {
    res = await fetch('/config.json', { cache: 'no-cache', signal: deadline.signal });
  } catch {
    throw deadline.timedOut() ? new TimeoutError() : new Error('config.json is unreachable');
  } finally {
    deadline.done();
  }
  if (!res.ok) throw new Error(`config.json could not be loaded (${res.status})`);
  current = parseRuntimeConfig(await res.json());
  return current;
}

export function runtimeConfig(): RuntimeConfig {
  if (current === null) throw new Error('runtime config read before load');
  return current;
}

/** Test seam. */
export function setRuntimeConfigForTests(cfg: RuntimeConfig | null): void {
  current = cfg;
}
